import { Collapse } from 'flowbite';
import type { CollapseOptions, CollapseInterface, InstanceOptions } from 'flowbite';
import { breakPoints } from '../../../config/saas-structure';
import { promoTab } from './SP99_2-promo_tab';

type MainNavTargets = {
    [property: string] : HTMLElement
};

export const otMainNavLegacy = {
    reduceMotion: window.matchMedia(`(prefers-reduced-motion: reduce)`) && window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true,
    scrollBehavior: getComputedStyle(document.documentElement).scrollBehavior,
    lastScrollTop: 0,
    isNavJustClosed: false,

    async init() {
        const $targets = this.getTargets();

        if (!$targets.mainNav) {
            return;
        };

        promoTab.init();

        const collapse = this.configureCollapse($targets);

        this.addEventListeners($targets, collapse);
        this.addScrollListener($targets, collapse);

        setTimeout(() => {
            document.querySelector('html')?.classList.add('scroll-smooth', 'scroll-pt-0')
        }, 1000)
    },
    getTargets(): MainNavTargets {
        const $mainNav = document.getElementById('main-nav') as HTMLElement;
        const collapsibleId = 'navbar-collapsible';
        
        if (!$mainNav) {
            return {};
        }

        return {
            mainNav: $mainNav,
            mainNavHide: $mainNav.closest('[data-ot-main-nav-hide]') as HTMLElement,
            collapsible: $mainNav?.querySelector(`#${collapsibleId}`) as HTMLButtonElement,
            collapseTransition: $mainNav?.querySelector('.js-collapse-transition') as HTMLElement,
            toggleBtn: $mainNav?.querySelector('[data-ot-toggle-collapse]') as HTMLButtonElement,
            signInBtn: $mainNav?.querySelector('.js-signin-btn-desktop') as HTMLElement,
            collapsibleNavItems: $mainNav?.querySelector('.collapsible-nav-items') as HTMLElement,
            signinMenuMobile: $mainNav?.querySelector('.signin-menu-mobile') as HTMLElement,
        }
    },
    configureCollapse($targets: MainNavTargets) {
        const $navbarCollapsible = document.getElementById('navbar-collapsible');
        const $toggleBtn = $targets.toggleBtn;
        const $toggleBtnText = $toggleBtn?.querySelector('[data-ot-swap-content]') as HTMLElement;
        const initContent = $toggleBtnText?.innerText;
        const swapContent = $toggleBtnText.dataset.otSwapContent || initContent;

        const options: CollapseOptions = {
            onCollapse: (_) => {
                // this.setScrollPadding($targets.mainNav);
                this.hideSigninMenuMobile($targets);
                this.updateToggleButton($toggleBtn, 'close');

                $toggleBtnText.innerHTML = initContent;
            },
            onExpand: (_) => {
                this.updateToggleButton($toggleBtn, 'open');

                // Settimeout allows animation to occur after collapseTransition is unhidden
                setTimeout(() => {
                    $targets.collapseTransition.classList.add($targets.collapseTransition.dataset.addClass as string);
                    $targets.collapseTransition.classList.remove($targets.collapseTransition.dataset.removeClass as string);
                });

                const transitionEnd = () => {
                    // this.setScrollPadding($targets.mainNav);
                    $targets.collapseTransition.removeEventListener('transitionend', transitionEnd)
                }

                $targets.collapseTransition.addEventListener('transitionend', transitionEnd);

                $toggleBtnText.innerHTML = swapContent;
            },
        };

        this.setScrollPadding($targets.mainNav);

        const instanceOptions: InstanceOptions = {
            id: 'navbar-collapsible-instance',
            override: false
        };

        return new Collapse($navbarCollapsible, null, options, instanceOptions);
    },
    updateToggleButton($toggleBtn: HTMLElement, action: 'open' | 'close') {
        const $toggleLine  = $toggleBtn.querySelector('.navbar-toggle-line');
        if (!$toggleLine) return;

        if (action === 'open') {
            $toggleLine.classList.add('bg-transparent', 'before:rotate-45', 'after:-rotate-45');
            $toggleLine.classList.remove('bg-white', 'before:-translate-y-[.45em]', 'after:translate-y-[.45em]', 'after:scale-x-[85%]', 'after:origin-bottom-left', 'before:scale-x-[85%]', 'before:origin-top-left');
        } else {
            $toggleLine.classList.remove('bg-transparent', 'before:rotate-45', 'after:-rotate-45');
            $toggleLine.classList.add('bg-white', 'before:-translate-y-[.45em]', 'after:translate-y-[.45em]', 'after:scale-x-[85%]', 'after:origin-bottom-left', 'before:scale-x-[85%]', 'before:origin-top-left');
        }
    },
    isDesktopBreakpoint() {
        return matchMedia(`(min-width: ${breakPoints.lg.minWidth}px)`).matches;
    },
    setScrollPadding($mainNav: HTMLElement) {
        $mainNav as HTMLElement;

        if (this.scrollBehavior === 'smooth') {
            // Update scroll-padding-top css on <html> element as the expanded nav is taken into account when calculated the offset
            // document.documentElement.style.scrollPaddingTop = $mainNav.offsetHeight + 32 + 'px';

            if (this.isDesktopBreakpoint()) {
                document.documentElement.style.scrollPaddingTop = '0';
            } else {
                // document.documentElement.style.scrollPaddingTop = $mainNav.offsetHeight + 'px';
            }
        }
    },
    setDropdownMenuPosition($dropdownTarget: HTMLElement, $mainNav: HTMLElement) {
        // Ensure right side of menu never passes right side of main nav element
        const $dropdownList = $dropdownTarget?.getElementsByTagName('ul')[0];
        const $container = $mainNav.querySelector<HTMLElement>('.container');

        $dropdownTarget.style.marginLeft = '0'; // Reset it so it can calculate again

        if (!$dropdownList) {
            return;
        }

        const ulBoundsRight = $dropdownList.getBoundingClientRect().right || 0;
        const containerBoundsRight = $container?.getBoundingClientRect().right || 0;
        const containerPaddingRight = $container && parseInt(window.getComputedStyle($container).paddingRight) || 0;
        const rightBoundary = containerBoundsRight - containerPaddingRight;

        if (ulBoundsRight > rightBoundary) {
            const offset = rightBoundary - ulBoundsRight;
            $dropdownTarget.style.marginLeft = offset + 'px';
        }
    },
    openDropdown($dropdownTarget: HTMLElement, $mainNav: HTMLElement) {
        const $dropdownTrigger = document.querySelector(`[data-ot-dropdown-id="${$dropdownTarget.id}"`) as HTMLElement;
        const $arrowIcon = $dropdownTrigger?.querySelector('.arrow-icon') as HTMLElement;

        $arrowIcon?.classList.add('rotate-180')
        $dropdownTarget.hidden = false;
        if (this.isDesktopBreakpoint()) {
            this.setDropdownMenuPosition($dropdownTarget, $mainNav);
        }
    },
    closeDropdown($dropdownTarget: HTMLElement) {
        const $dropdownTrigger = document.querySelector(`[data-ot-dropdown-id="${$dropdownTarget.id}"`) as HTMLElement;
        const $arrowIcon = $dropdownTrigger?.querySelector('.arrow-icon') as HTMLElement;

        $arrowIcon && $arrowIcon.classList.remove('rotate-180');
        $dropdownTarget.hidden = true;
    },
    closeDropdowns($targets: MainNavTargets, $trigger?: HTMLElement) {
        const $dropdownTargets = $targets.mainNav?.querySelectorAll('.navbar-L2-dropdown') as NodeList;

        $dropdownTargets?.forEach((dropdownTarget) => {
            const $dropdownTarget = dropdownTarget as HTMLElement;
            // Ignore if trigger is that of current dropdown
            if (!$trigger || ($trigger && $trigger?.dataset.otDropdownId !== $dropdownTarget.id)) {
                this.closeDropdown($dropdownTarget);
            }
        });
    },
    handleDropdownClicks($dropdownTarget: HTMLElement, $targets: MainNavTargets) {
        if ($dropdownTarget.hidden) {
            this.openDropdown($dropdownTarget, $targets.mainNav)
        } else {
            this.closeDropdown($dropdownTarget);
        }
    },
    showSigninMenuMobile($targets: MainNavTargets) {
        $targets.collapsibleNavItems.hidden = true;
        $targets.signinMenuMobile.hidden = false;
        $targets.signInBtn?.querySelector('.arrow-icon')?.classList.add('rotate-180');
    },
    hideSigninMenuMobile($targets: MainNavTargets) {
        $targets.collapsibleNavItems.hidden = false;
        $targets.signinMenuMobile.hidden = true;
        $targets.signInBtn?.querySelector('.arrow-icon')?.classList.remove('rotate-180');
    },
    handleSigninButtonClick($targets: MainNavTargets, collapse: CollapseInterface) {
        if ($targets.signinMenuMobile.hidden) {
            this.showSigninMenuMobile($targets);

            if (!collapse._visible) {
                $targets.toggleBtn?.click();
            }
        } else {
            $targets.toggleBtn?.click();
        }
    },
    collapse($targets: MainNavTargets, collapse: CollapseInterface) {
        // Transition collapse element to closed state
        $targets.collapseTransition.classList.remove($targets.collapseTransition.dataset.addClass as string);
        $targets.collapseTransition.classList.add($targets.collapseTransition.dataset.removeClass as string);

        // Transition toggle element to closed state
        const $toggleBtn = $targets.toggleBtn;
        this.updateToggleButton($toggleBtn, 'close');

        // Add collapse transition before calling collapse
        const transitionEnd = () => {
            collapse.collapse();
            this.isNavJustClosed = true;

            // Reset isNavJustClosed after a short delay
            setTimeout(() => {
                this.isNavJustClosed = false;
            }, 100);

            $targets.collapseTransition.removeEventListener('transitionend', transitionEnd);
        };

        if (this.isDesktopBreakpoint() || this.reduceMotion) {
            // Transitionend won't be triggered if transitioned element is not visible
            collapse.collapse();
        } else {
            $targets.collapseTransition.addEventListener('transitionend', transitionEnd)
        }
    },
    addEventListeners($targets: MainNavTargets, collapse: CollapseInterface) {
        $targets.toggleBtn?.addEventListener('click', () => {
            if (collapse._visible) {
                this.collapse($targets, collapse)
            } else {
                collapse.expand();
            }
        });

        window.addEventListener('click', (e) => {
            const $trigger = e.target as HTMLElement;
            const dropdownTargetId = $trigger.dataset.otDropdownId;
            const $dropdownTarget = dropdownTargetId && document.getElementById(dropdownTargetId);
            const handleSigninButtonClick = dropdownTargetId === 'signin-menu-dropdown' && !this.isDesktopBreakpoint();

            if (handleSigninButtonClick) {
                this.handleSigninButtonClick($targets, collapse);
            } else if ($dropdownTarget) {
                this.closeDropdowns($targets, $trigger);
                this.handleDropdownClicks($dropdownTarget, $targets);
            } else if ($trigger.classList.contains('js-back-btn')) {
                this.hideSigninMenuMobile($targets);
            } else if ($trigger?.classList.contains('js-signin-btn-mobile')) {
                this.showSigninMenuMobile($targets);
            } else if ($trigger?.dataset.otModalShow) {
                this.collapse($targets, collapse); // Always collapse nav when any modal toggle is clicked
            } else if (!$trigger.closest('[data-ot-main-nav]') || $trigger.hasAttribute('href')) {
                // Close collapsible and dropdowns when clicking outside of main nav
                collapse._visible && $targets.toggleBtn?.click();
                this.closeDropdowns($targets);
            }
        });

        let resizeDebounce: number | undefined | ReturnType<typeof setTimeout> = 0;

        window.addEventListener('resize', () => {
            clearTimeout(resizeDebounce);
            resizeDebounce = setTimeout(() => {
                if (this.isDesktopBreakpoint() && collapse._visible) {
                    // Close collapsible when resized to >= lg
                    $targets.toggleBtn?.click();
                } else {
                    // Close dropdowns when resized to < lg
                    this.closeDropdowns($targets);
                }

                this.setScrollPadding($targets.mainNav);
            }, 200);
        });
    },
    addScrollListener($targets: MainNavTargets, collapse: CollapseInterface) {
        if (!$targets.mainNavHide) return;

        window.addEventListener('scroll', () => {
            if (collapse._visible || this.isNavJustClosed) return; // Don't hide nav when it's open

            const scrollTop = window.scrollY || document.documentElement.scrollTop;

            if (scrollTop > this.lastScrollTop && scrollTop > 50) {
                // Scrolling down
                $targets.mainNavHide.classList.add('transition-transform', 'duration-300', 'ease-in-out');
                $targets.mainNavHide.classList.add('-translate-y-full');
                $targets.mainNavHide.classList.remove('translate-y-0');
            } else {
                // Scrolling up
                $targets.mainNavHide.classList.remove('-translate-y-full');
                $targets.mainNavHide.classList.add('translate-y-0');
            }

            this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
        });
    }
}
